import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { setReturnMethod, ReturnMethod } from '@/redux/return/returnSlice'
import { useTheme } from '@itsrever/design-system'
import styled from 'styled-components'
import { PkgInfrastructureRestReturnMethod as RestReturnMethod } from '@itsrever/returns-api-types'
import Sustainable from '@/assets/images/icons/sustainable.svg'
import DeliveryPickup from '@/assets/images/icons/delivery_pickup.svg'
import Dropoff from '@/assets/images/icons/dropoff.svg'
import HomePickup from '@/assets/images/icons/homepickup.svg'
import InStore from '@/assets/images/icons/instore.svg'
import Search from '@/assets/images/icons/search.svg'
import { formatCurrency, toRGB } from '@/utils'
import { useTranslate } from '@/hooks'
import clsx from 'clsx'

interface ReturnCardProps {
    method: RestReturnMethod
}

const ReturnCard: React.FC<ReturnCardProps> = ({ method }) => {
    const { translate } = useTranslate()
    const dispatch = useAppDispatch()
    const theme = useTheme()
    const returnMethod = useAppSelector((store) => store.return.returnMethod)
    const settings = useAppSelector(
        (store) => store.settingsApi.settings.response
    )

    const selected = returnMethod === method.name

    let id: string | undefined
    if (method.name) {
        id = method.name.toString()
    }

    const iconSource = () => {
        switch (method.name) {
            case ReturnMethod.CollectionPoint:
                return Dropoff
            case ReturnMethod.DeliveryPickup:
                return DeliveryPickup
            case ReturnMethod.HomePickup:
                return HomePickup
            case ReturnMethod.InStore:
                return InStore
        }
    }

    const greenOption = method.name === ReturnMethod.InStore

    const showButton =
        method.name === ReturnMethod.InStore ||
        method.name === ReturnMethod.CollectionPoint

    const href = () => {
        if (method.name === ReturnMethod.CollectionPoint) {
            return method.drop_off_point_url
        } else {
            return settings.store_list_url
        }
    }
    const isFree = parseInt(method.costs?.amount || '0') === 0
    const price = formatCurrency(
        method.costs?.amount || '0',
        method.costs?.currency || 'EUR'
    )

    const isMisako = settings.ecommerce_group == 'misako'
    const isHoff = settings.ecommerce_group == 'hoff'

    if (isHoff) {
        return (
            <div
                id={id}
                className="relative flex flex-col content-center items-center border-b-1 border-solid border-black"
                onClick={() =>
                    dispatch(
                        setReturnMethod({
                            returnMethod: method.name as ReturnMethod,
                            userSelectedCarrier: method.carrier ?? '',
                            userSelectedProvider: method.provider ?? ''
                        })
                    )
                }
            >
                {greenOption ? (
                    <GreenOptionLabel>
                        <img src={Sustainable} />
                        <p className="text-xs">
                            <b
                                dangerouslySetInnerHTML={{
                                    __html: translate(
                                        'return_card_green_option'
                                    )
                                }}
                            />
                        </p>
                    </GreenOptionLabel>
                ) : null}
                {(isFree && method.costs) || greenOption ? (
                    <ZeroCurve
                        $isGreenOption={greenOption}
                        $textColor={theme.colors.primary.contrastText}
                        $bgColor={theme.colors.primary.main}
                    >
                        <p
                            className="whitespace-nowrap text-xs"
                            dangerouslySetInnerHTML={{
                                __html: translate('return_card_free')
                            }}
                        />
                    </ZeroCurve>
                ) : null}
                <div
                    className={clsx(
                        'w-full cursor-pointer border-t-1 border-solid border-black px-4 py-3 hover:bg-neutral-100',
                        selected && 'bg-neutral-100'
                    )}
                >
                    <div className="w-full">
                        <div className="mb-2 flex items-center gap-2">
                            <span>
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fill="currentColor"
                                        d="M10.115 21.811c.606.5 1.238.957 1.885 1.403a27 27 0 0 0 1.885-1.403a28 28 0 0 0 2.853-2.699C18.782 16.877 21 13.637 21 10a9 9 0 1 0-18 0c0 3.637 2.218 6.876 4.262 9.112a28 28 0 0 0 2.853 2.7M12 13.25a3.25 3.25 0 1 1 0-6.5a3.25 3.25 0 0 1 0 6.5"
                                    />
                                </svg>
                            </span>
                            <p className="text-lg">
                                <span
                                    className="font-haas font-light"
                                    dangerouslySetInnerHTML={{
                                        __html: translate(
                                            `return_card_method${method.name}_title`
                                        )
                                    }}
                                />
                            </p>
                        </div>
                        {method.costs ? (
                            <p className="font-haas text-sm font-light text-neutral-500">
                                {translate('return_card_fee')}: {price}
                            </p>
                        ) : null}
                        <p
                            className="font-haas font-normal"
                            style={{
                                color: selected
                                    ? theme.colors.common.black
                                    : theme.colors.grey[0]
                            }}
                            dangerouslySetInnerHTML={{
                                __html: translate(
                                    `return_card_method${method.name}_description`
                                )
                            }}
                        />
                    </div>
                    {showButton ? (
                        <div className="mt-4 flex">
                            <a
                                target="_blank"
                                href={href()}
                                rel="noreferrer"
                                className="flex items-center gap-2 text-black underline-offset-2 transition-all hover:underline"
                            >
                                <span>
                                    <svg
                                        aria-hidden="true"
                                        fill="none"
                                        focusable="false"
                                        width="24"
                                        viewBox="0 0 24 24"
                                        className="size-4"
                                    >
                                        <path
                                            d="M10.364 3a7.364 7.364 0 1 0 0 14.727 7.364 7.364 0 0 0 0-14.727Z"
                                            stroke="currentColor"
                                            strokeWidth="1"
                                            strokeMiterlimit="10"
                                        ></path>
                                        <path
                                            d="M15.857 15.858 21 21.001"
                                            stroke="currentColor"
                                            strokeWidth="1"
                                            strokeMiterlimit="10"
                                            strokeLinecap="round"
                                        ></path>
                                    </svg>
                                </span>
                                <p
                                    className="font-haas text-xs"
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            method.name === ReturnMethod.InStore
                                                ? translate(
                                                      'return_card_in_store_link'
                                                  )
                                                : translate(
                                                      'return_card_dropoff_link'
                                                  )
                                    }}
                                />
                            </a>
                        </div>
                    ) : null}
                </div>
            </div>
        )
    }

    if (isMisako) {
        return (
            <div
                id={id}
                className="relative flex w-full flex-1 xl:max-w-64"
                onClick={() =>
                    dispatch(
                        setReturnMethod({
                            returnMethod: method.name as ReturnMethod,
                            userSelectedCarrier: method.carrier ?? '',
                            userSelectedProvider: method.provider ?? ''
                        })
                    )
                }
            >
                <div className="mb-1">
                    <div
                        className={clsx(
                            'flex size-full flex-1 cursor-pointer flex-col rounded-sm border border-solid border-neutral-300 p-4 transition-all hover:border-neutral-400 hover:bg-neutral-100/60',
                            { 'border-neutral-400 bg-neutral-100/60': selected }
                        )}
                    >
                        <div className="w-full">
                            <div className="mb-1 flex justify-between gap-2">
                                <p
                                    className="mt-0.5 text-[15px] font-[300] tracking-wider [font-family:Inter]"
                                    dangerouslySetInnerHTML={{
                                        __html: translate(
                                            `return_card_method${method.name}_title`
                                        )
                                    }}
                                />
                                {method.name === 4 ? (
                                    <svg
                                        width="22px"
                                        height="22px"
                                        strokeWidth="1.5"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        color="#000000"
                                    >
                                        <path
                                            d="M16 9.2C16 13.1765 9 20 9 20C9 20 2 13.1765 2 9.2C2 5.22355 5.13401 2 9 2C12.866 2 16 5.22355 16 9.2Z"
                                            stroke="#000000"
                                            strokeWidth="1.5"
                                        ></path>
                                        <path
                                            d="M16 19L22 19"
                                            stroke="#000000"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        ></path>
                                        <path
                                            d="M9 10C9.55228 10 10 9.55228 10 9C10 8.44772 9.55228 8 9 8C8.44772 8 8 8.44772 8 9C8 9.55228 8.44772 10 9 10Z"
                                            fill="#000000"
                                            stroke="#000000"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        ></path>
                                    </svg>
                                ) : null}
                            </div>
                            {method.costs ? (
                                <p className="mb-2 mt-3 flex items-center text-xs font-[300] tracking-wider text-neutral-400 [font-family:Inter]">
                                    {translate('return_card_fee')}: {price}
                                </p>
                            ) : null}
                            <p
                                className="text-[13px] font-[300] leading-5 tracking-wider [font-family:Inter]"
                                style={{
                                    color: selected
                                        ? theme.colors.common.black
                                        : theme.colors.grey[0]
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: translate(
                                        `return_card_method${method.name}_description`
                                    )
                                }}
                            />
                        </div>
                        {showButton ? (
                            <div className="mt-auto flex justify-center pt-4">
                                <ActionLink
                                    bgColor="transparent"
                                    col="#262626"
                                    isGreenOption={false}
                                    target="_blank"
                                    href={href()}
                                    className="!rounded-none !px-2.5 !py-1.5 underline-offset-8"
                                >
                                    <p
                                        className="text-center text-[10px] leading-snug tracking-wider [font-family:Inter]"
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                method.name ===
                                                ReturnMethod.InStore
                                                    ? translate(
                                                          'return_card_in_store_link'
                                                      )
                                                    : translate(
                                                          'return_card_dropoff_link'
                                                      )
                                        }}
                                    />
                                </ActionLink>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div
            id={id}
            className="relative flex flex-col content-center items-center"
            onClick={() =>
                dispatch(
                    setReturnMethod({
                        returnMethod: method.name as ReturnMethod,
                        userSelectedCarrier: method.carrier ?? '',
                        userSelectedProvider: method.provider ?? ''
                    })
                )
            }
        >
            {greenOption ? (
                <GreenOptionLabel>
                    <img src={Sustainable} />
                    <p className="text-xs">
                        <b
                            dangerouslySetInnerHTML={{
                                __html: translate('return_card_green_option')
                            }}
                        />
                    </p>
                </GreenOptionLabel>
            ) : null}
            {(isFree && method.costs) || greenOption ? (
                <ZeroCurve
                    $isGreenOption={greenOption}
                    $textColor={theme.colors.primary.contrastText}
                    $bgColor={theme.colors.primary.main}
                >
                    <p
                        className="whitespace-nowrap text-xs"
                        dangerouslySetInnerHTML={{
                            __html: translate('return_card_free')
                        }}
                    />
                </ZeroCurve>
            ) : null}
            <div className="mb-1 flex items-center">
                <Card
                    selected={selected}
                    isGreenOption={greenOption}
                    bgColor={theme.colors.primary.main}
                    col={toRGB(theme.colors.primary.main)}
                >
                    <div className="w-full">
                        <div className="mb-2 flex items-center gap-2">
                            <Icon src={iconSource()} selected={selected} />
                            <p className="text-lg">
                                <b
                                    dangerouslySetInnerHTML={{
                                        __html: translate(
                                            `return_card_method${method.name}_title`
                                        )
                                    }}
                                />
                            </p>
                        </div>
                        {method.costs ? (
                            <p className="pb-1 font-semibold text-orange-400">
                                {translate('return_card_fee')}: {price}
                            </p>
                        ) : null}
                        <p
                            className="font-normal"
                            style={{
                                color: selected
                                    ? theme.colors.common.black
                                    : theme.colors.grey[0]
                            }}
                            dangerouslySetInnerHTML={{
                                __html: translate(
                                    `return_card_method${method.name}_description`
                                )
                            }}
                        />
                    </div>
                    {showButton ? (
                        <div className="mt-4 flex justify-center">
                            <ActionLink
                                bgColor={theme.colors.primary.main}
                                col={theme.colors.primary.contrastText}
                                isGreenOption={greenOption}
                                target="_blank"
                                href={href()}
                            >
                                <img src={Search} />
                                <p
                                    className="text-xs"
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            method.name === ReturnMethod.InStore
                                                ? translate(
                                                      'return_card_in_store_link'
                                                  )
                                                : translate(
                                                      'return_card_dropoff_link'
                                                  )
                                    }}
                                />
                            </ActionLink>
                        </div>
                    ) : null}
                </Card>
            </div>
        </div>
    )
}

export default ReturnCard

const ActionLink = styled.a<{
    bgColor: string
    col: string
    isGreenOption: boolean
}>`
    background-color: ${(p) => (p.isGreenOption ? '#20904d' : p.bgColor)};
    color: ${(p) => (p.isGreenOption ? 'white' : p.col)};
    border-radius: 20px;
    padding: 0.2rem 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.35rem;
    :hover {
        text-decoration: underline;
    }
`

const ZeroCurve = styled.div<{
    $isGreenOption: boolean
    $textColor: string
    $bgColor: string
}>`
    color: ${(p) => (p.$isGreenOption ? 'white' : p.$textColor)};
    display: flex;
    justify-content: center;
    position: absolute;
    top: 0;
    right: 0px;
    height: 25px;
    padding: 0.2rem 0.4rem 0.2rem 0.75rem;
    border-top-right-radius: 0.25rem;
    border-bottom: 1px solid
        ${(p) => (p.$isGreenOption ? '#20904d' : p.$bgColor)};
    border-bottom-left-radius: 70px;
    background-color: ${(p) => (p.$isGreenOption ? '#20904d' : p.$bgColor)};
`

const Icon = styled.img<{ selected: boolean }>`
    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        50% {
            transform: rotate(-30deg);
        }
        100% {
            transform: rotate(0deg);
        }
    }
    animation: ${(p) => (p.selected ? 'spin 0.5s linear' : '')};

    height: 15px;
    width: 15px;
`

const GreenOptionLabel = styled.div`
    position: absolute;
    top: -10px;
    left: 16px;
    padding: 0.1rem 0.8rem;
    border: 1px solid #20904d;
    border-radius: 20px;
    background: radial-gradient(circle at 78px 10px, #20904d, #4caf50 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.3rem;
`

interface CardProps {
    selected: boolean
    isGreenOption: boolean
    bgColor: string
    col: string
}

const Card = styled.div<CardProps>`
    padding: ${(p) => (p.selected ? '12px 10px' : '14px 12px')};
    cursor: pointer;
    border: ${(p) => (p.selected ? '2px' : '0px')} solid;
    border-radius: 0.25rem;
    width: 100%;
    margin-bottom: 1.5rem;
    border-color: ${(p) =>
        p.selected ? (p.isGreenOption ? '#20904d' : p.bgColor) : ''};
    background-color: ${(p) =>
        p.selected ? (p.isGreenOption ? 'rgb(0, 117, 0, 0.1)' : p.col) : ''};
    box-shadow: ${(p) =>
        p.selected ? '' : '0px 0px 2px 2px rgb(0 0 0 / 0.1)'};
`
